// src/components/SubscriptionManagement/SubscriptionManagement.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, CircularProgress } from "@mui/material";
import ConsolidatedPlansScreen from "./ConsolidatedPlansScreen";
import AddOnsSection from "./AddOnsSection";
import CheckoutSection from "./CheckoutSection";
import AddonPurchaseCheckout from "./AddonPurchaseCheckout";
import BillingHistory from "./BillingHistory";
import TransactionDetails from "./TransactionDetails";

import {
  GET_TENANT_PLANS,
  GET_SUBSCRIPTIONS,
  GET_TENANT_ADDONS,
  GET_TENANT_DETAILS,
} from "../../config/urls.js";
import { useAuth } from "../../hooks/AuthProvider.js";

const UPDATE_SUBSCRIPTION_API = "/api/tenant/subscription/";
const MF_PLAN_NAMES = ["elite", "pro"];

const SubscriptionManagement = ({ onNavigate, pageParams }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");
  const [currentTab, setCurrentTab] = useState(0);

  // const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState({
    plans: false,
    addons: false,
    subscription: false,
    checkout: false,
    history: false,
    pricing: false,
  });
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Data states
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [currentAddons, setCurrentAddons] = useState([]);

  // Selection states
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState("quarterly");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [checkoutPage, setCheckoutPage] = useState(false);
  const [addonPurchaseMode, setAddonPurchaseMode] = useState(false);
  const [billingCurrency, setBillingCurrency] = useState("INR");
  useEffect(() => {
    loadInitialData();
    loadAddonsData();
    fetchTenantDetails();
  }, []);

  const fetchTenantDetails = async () => {
    try {
      const token = await getValidToken();
      const tenantId = localStorage.getItem("tenant");

      const response = await fetch(GET_TENANT_DETAILS, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-ID": tenantId,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const tenantCurrency = data?.results?.[0]?.billing_currency;
      if (tenantCurrency) {
        setBillingCurrency(tenantCurrency);
      }
    } catch (error) {
      console.error("Error fetching tenant details:", error);
    }
  };

  const loadInitialData = async () => {
    try {
      // Load plans and pricing from Default DB (catalog)
      await loadPlansAndPricing();
    } catch (err) {
      const errorMessage =
        err?.message || "Failed to load subscription data. Please try again.";
      setError(errorMessage);
      console.error("Error loading data:", err);
    }
  };

  const loadPlansAndPricing = async () => {
    const token = await getValidToken();

    try {
      setLoading((prev) => ({ ...prev, plans: true, pricing: true }));

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      // Load plans
      const plansResponse = await fetch(GET_TENANT_PLANS, {
        headers,
      });

      if (!plansResponse?.ok) {
        throw new Error(
          `Failed to load plans: ${plansResponse?.status || "Unknown error"}`,
        );
      }

      const plansData = await plansResponse.json();
      const plansList = plansData?.results || [];

      // Set plans state immediately
      setPlans(plansList);

      // Now load current subscription WITH the plansList we just got
      await loadCurrentSubscription(plansList); // PASS THE PLANS LIST HERE
    } catch (err) {
      throw err;
    } finally {
      setLoading((prev) => ({ ...prev, plans: false, pricing: false }));
    }
  };

  const loadCurrentSubscription = async (plansFromCatalog = plans) => {
    const token = await getValidToken();

    try {
      setLoading((prev) => ({ ...prev, subscription: true }));

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const subscriptionResponse = await fetch(GET_SUBSCRIPTIONS, {
        headers,
      });

      if (subscriptionResponse?.ok) {
        const currentSub = await subscriptionResponse.json();

        setCurrentSubscription(currentSub);

        const currentPlan = currentSub?.plan;

        if (currentPlan) {
          setSelectedPlan(currentPlan);
        } else {
          console.log(`Subscription has no plan`);
        }

        const currentAddons = currentSub?.addons;
        const addonQuantities = currentSub?.addon_quantities || {};

        if (currentAddons) {
          // Merge stored quantities into each addon object so the UI knows the
          // current quantity without an extra API call.
          const addonsWithQty = currentAddons.map((addon) => ({
            ...addon,
            currentQuantity: addonQuantities[String(addon.id)] ?? 1,
          }));
          setCurrentAddons(addonsWithQty);
        } else {
          console.log(`Subscription has no addons`);
        }

        // Determine billing cycle — always honour the subscription's stored cycle,
        // including trial (billing cycle is set server-side from the promo code).
        if (currentSub?.billing_cycle) {
          setSelectedBillingCycle(currentSub?.billing_cycle);
        }
      } else if (subscriptionResponse?.status === 404) {
        console.log("No existing subscription found");
      } else {
        console.warn(
          `Failed to load subscription: ${subscriptionResponse?.status || "Unknown error"}`,
        );
      }
    } catch (err) {
      console.error("Unexpected error loading subscription:", err);
    } finally {
      setLoading((prev) => ({ ...prev, subscription: false }));
    }
  };

  const loadAddonsData = async () => {
    const token = await getValidToken();

    try {
      setLoading((prev) => ({ ...prev, addons: true }));

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const response = await fetch(GET_TENANT_ADDONS, {
        headers,
      });

      if (!response?.ok) {
        throw new Error(
          `Failed to load addons: ${response?.status || "Unknown error"}`,
        );
      }

      const addonsData = await response.json();
      const addonsList = addonsData?.results || [];
      setAddons(addonsList);
    } catch (err) {
      const errorMessage = err?.message || "Failed to load addons";
      setError(errorMessage);
      console.error("Error loading addons:", err);
    } finally {
      setLoading((prev) => ({ ...prev, addons: false }));
    }
  };

  const handlePlanSelect = async (plan) => {
    if (!plan) {
      console.error("Invalid plan selected");
      return;
    }

    const selectedPlanIsMF = MF_PLAN_NAMES.includes(plan?.name);

    setSelectedPlan(plan);
    setSelectedAddons([]);

    if (selectedPlanIsMF) {
      // MF plans skip add-ons and go straight to checkout
      setCurrentTab(2);
      setCheckoutPage(true);
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, addons: true }));
      await loadAddonsData();
      setCurrentTab(2);
    } catch (err) {
      console.error("Error loading addons:", err);
      setCurrentTab(2);
    } finally {
      setLoading((prev) => ({ ...prev, addons: false }));
    }
  };

  const handleAddonToggle = (addon, isSelected) => {
    if (!addon) {
      console.error("Invalid addon");
      return;
    }

    if (isSelected) {
      setSelectedAddons((prev) => {
        const list = prev || [];
        const existingIndex = list.findIndex((a) => a?.id === addon.id);
        if (existingIndex !== -1) {
          const updated = [...list];
          updated[existingIndex] = addon;
          return updated;
        }
        return [...list, addon];
      });
    } else {
      setSelectedAddons((prev) =>
        (prev || []).filter((a) => a?.id !== addon.id),
      );
    }
  };

  const handleRemoveAddon = (addonId) => {
    if (!addonId) return;
    setSelectedAddons((prev) => (prev || []).filter((a) => a?.id !== addonId));
  };

  const handleBillingCycleChange = (cycle) => {
    if (!cycle) return;
    setSelectedBillingCycle(cycle);
  };

  // Renew current plan in-place (same plan + existing addons → direct to checkout).
  // Used for "Renew Now" (renewal_due) and "Pay & Renew" (past_due) CTAs.
  const handleRenewCurrentPlan = (plan) => {
    const p = plan || currentSubscription?.plan;
    if (!p) return;
    setSelectedPlan(p);
    setSelectedBillingCycle(currentSubscription?.billing_cycle || "quarterly");
    setSelectedAddons((currentAddons || []).filter((a) => a.addon_type !== "one_time"));
    setCurrentTab(2);
    setCheckoutPage(true);
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccessMessage(null);
  };

  const handleCheckoutButton = () => {
    setCheckoutPage(true);
  };

  const handleManageAddons = () => {
    setAddonPurchaseMode(true);
    setSelectedAddons([]);
    setCurrentTab(2);
    setCheckoutPage(false);
  };

  const isMutualFundPlan = MF_PLAN_NAMES.includes(localStorage.getItem("plan"));

  const isActiveStatus = useMemo(() => {
    return currentSubscription?.status === "active";
  }, [currentSubscription]);

  const isTrialStatus = useMemo(() => {
    return currentSubscription?.status === "trial";
  }, [currentSubscription]);

  // "Purchase Add-on" on a locked page lands straight on add-on selection, when the plans screen would offer it.
  // Otherwise (trial/expired/MF plan) the user stays on the plans screen to renew or upgrade.
  const openedAddons = useRef(false);
  useEffect(() => {
    if (!pageParams?.openAddons || openedAddons.current || !currentSubscription) return;
    openedAddons.current = true;
    if (!isMutualFundPlan && isActiveStatus) handleManageAddons();
  }, [currentSubscription]);

  if (loading.plans && !currentSubscription) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f8f9fa",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: 3,
        mt: -2,
        mb: -4,
        py: 2,
        backgroundColor: "#F7F7F7",
      }}
    >
      {currentTab === 0 && (
        <Box sx={{ mt: 2 }}>
          <ConsolidatedPlansScreen
            currentSubscription={currentSubscription}
            currentAddons={currentAddons}
            plans={plans || []}
            selectedBillingCycle={selectedBillingCycle}
            billingCurrency={billingCurrency}
            onBillingCycleChange={handleBillingCycleChange}
            onPlanSelect={handlePlanSelect}
            onRenewCurrentPlan={handleRenewCurrentPlan}
            onManageAddons={(!isMutualFundPlan && isActiveStatus) ? handleManageAddons : undefined}
            onViewBillingHistory={() => {
              if (onNavigate) {
                localStorage.setItem("invoiceDefaultTab", "1");
                window.dispatchEvent(new CustomEvent("set-invoice-tab", { detail: { tab: 1 } }));
                onNavigate("billing_invoices");
              }
            }}
            loading={loading.plans || loading.subscription}
          />
        </Box>
      )}

      <Box sx={{ py: 5 }}>
        {/* Tabs */}
        {/* <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                color: ColorsConstants?.primaryGray50 || "#666",
              },
              "& .Mui-selected": {
                color: ColorsConstants?.primaryPurple100 || "#673ab7",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: ColorsConstants?.primaryPurple100 || "#673ab7",
                height: 3,
              },
            }}
          >
            <Tab label="Plans & Pricing" />
            <Tab label="Add-ons" />
            <Tab label="Checkout" />
            <Tab label="Billing History" />
            <Tab label="Transaction Details" />
          </Tabs>
        </Box> */}
        {/* <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="error"
            sx={{ width: "100%" }}
          >
            {error}
          </Alert>
        </Snackbar> */}

        {/* <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="success"
            sx={{ width: "100%" }}
          >
            {successMessage}
          </Alert>
        </Snackbar> */}

        {/* Tab Content */}
        {currentTab === 2 && !checkoutPage && !isMutualFundPlan && (
          <AddOnsSection
            addons={
              addonPurchaseMode
                ? (addons || [])
                : (addons || []).filter((a) => a.addon_type !== 'one_time')
            }
            currentAddons={currentAddons || []}
            selectedAddons={selectedAddons || []}
            selectedPlan={selectedPlan}
            selectedBillingCycle={selectedBillingCycle}
            billingCurrency={billingCurrency}
            onAddonToggle={handleAddonToggle}
            onGoToPlans={
              addonPurchaseMode
                ? () => { setCurrentTab(0); setAddonPurchaseMode(false); }
                : () => setCurrentTab(0)
            }
            onContinueToCheckout={() => setCheckoutPage(true)}
            loading={loading.addons}
            checkoutPage={checkoutPage}
            setCheckoutPage={setCheckoutPage}
            addonPurchaseMode={addonPurchaseMode}
          />
        )}

        {currentTab === 2 && checkoutPage && addonPurchaseMode && (
          <AddonPurchaseCheckout
            selectedAddons={selectedAddons || []}
            currentSubscription={currentSubscription}
            billingCurrency={billingCurrency}
            onBack={() => setCheckoutPage(false)}
          />
        )}

        {currentTab === 2 && checkoutPage && !addonPurchaseMode && (
          <CheckoutSection
            selectedPlan={selectedPlan}
            selectedBillingCycle={selectedBillingCycle}
            selectedAddons={selectedAddons || []}
            billingCurrency={billingCurrency}
            currentSubscription={currentSubscription}
            onRemoveAddon={handleRemoveAddon}
            isMutualFundPlan={isMutualFundPlan}
            onAddMoreAddons={
              isMutualFundPlan
                ? () => {
                    setCheckoutPage(false);
                    setCurrentTab(0);
                  }
                : () => setCheckoutPage(false)
            }
            loading={loading.checkout}
            checkoutPage={checkoutPage}
            setCheckoutPage={setCheckoutPage}
          />
        )}

        {currentTab === 3 && (
          <TransactionDetails onBack={() => setCurrentTab(0)} billingCurrency={billingCurrency} />
        )}

        {/* {activeTab === 4 && (
          <TransactionDetails
          />
        )} */}
      </Box>
    </Box>
  );
};

export default SubscriptionManagement;
